// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

type ConfirmationDialogVariant = 'confirm' | 'delete' | 'update' | 'success';

type ConfirmationDialogProps = {
  open: boolean;
  variant?: ConfirmationDialogVariant;
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm?: () => void;
  onClose: () => void;
};

type VariantConfig = Record<
  ConfirmationDialogVariant,
  {
    title: string;
    confirmLabel: string;
    color: 'primary' | 'error' | 'success';
    icon: React.ReactNode;
  }
>;

function buildVariantConfig(t: (key: string) => string): VariantConfig {
  return {
    confirm: {
      title: t('confirmationDialog.confirm.title'),
      confirmLabel: t('confirmationDialog.confirm.confirmLabel'),
      color: 'primary',
      icon: <WarningAmberIcon color="primary" />,
    },
    delete: {
      title: t('confirmationDialog.delete.title'),
      confirmLabel: t('confirmationDialog.delete.confirmLabel'),
      color: 'error',
      icon: <DeleteOutlineIcon color="error" />,
    },
    update: {
      title: t('confirmationDialog.update.title'),
      confirmLabel: t('confirmationDialog.update.confirmLabel'),
      color: 'primary',
      icon: <WarningAmberIcon color="primary" />,
    },
    success: {
      title: t('confirmationDialog.success.title'),
      confirmLabel: t('confirmationDialog.success.confirmLabel'),
      color: 'success',
      icon: <CheckCircleOutlineIcon color="success" />,
    },
  };
}

export function ConfirmationDialog({
  open,
  variant = 'confirm',
  title,
  message,
  confirmLabel,
  cancelLabel,
  loading = false,
  onConfirm,
  onClose,
}: ConfirmationDialogProps) {
  const { t } = useTranslation('common');
  const variantConfig = buildVariantConfig(t);
  const config = variantConfig[variant];

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {config.icon}
        {title ?? config.title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>

      <DialogActions>
        {variant !== 'success' && (
          <Button onClick={onClose} disabled={loading}>
            {cancelLabel ?? t('confirmationDialog.cancelLabel')}
          </Button>
        )}

        <Button
          onClick={onConfirm ?? onClose}
          color={config.color}
          variant="contained"
          disabled={loading}
        >
          {confirmLabel ?? config.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
